import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import { MainForm } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { getReasonOptions } from '@Features/EmployeeSelfService/requests/ResignationRequests/store/selectors/resignationRequestSelector';
import {
  useGetSettlementAmountMutation,
  useRespondResignationRequestMutation,
} from '@Features/EmployeeSelfService/requests/ResignationRequests/store/api/resignationRequestApiSlice';
import {
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { createdUserRequestSelector } from '@Store/selectors/userInfoSelector';
import {
  FormApproveRejectRequest,
  ResignationRequestFormResponse,
} from '@Types/api/form';
import { FooterActions } from '@Enum/index';
import { NotificationContext } from '@Context/NotificationContext';
import ApproveList from '@Components/MainForm/components/ApproveList/ApproveList';
import { parseDateTime } from '@Utils/transformDateTime';
import { ReassignWindow } from '@Components/ReassignWindow';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';

import { initialFormValues } from '../initialValues';
import { prepareFormHeaderData } from '../utils';

interface ResignationRequestApproveFormProps {
  formData: ResignationRequestFormResponse;
  taskId: string;
}

const ResignationRequestApproveForm: React.FC<
  ResignationRequestApproveFormProps
> = ({ formData, taskId }) => {
  const {
    taskApprovedNotification,
    taskErrorNotification,
    taskRejectedNotification,
    errorNotification,
  } = useContext(NotificationContext);
  const { handleBackToListRequests } = useNavigationToListRequests();

  const [comments, setCommentValue] = useState('');
  const [isReassignOpen, setReassignOpen] = useState(false);

  const requestedUser = useAppSelector(createdUserRequestSelector);
  const reasonOptions = useAppSelector(getReasonOptions);

  const [respondRequest] = useRespondResignationRequestMutation();
  const [getSettlementAmount] = useGetSettlementAmountMutation();

  const formHeaderData = useMemo(() => {
    const output = prepareFormHeaderData(formData);
    return output;
  }, [formData]);
  const requisitionInfo = useRequisitionInfoExistingReq(
    formHeaderData,
    'Resignation'
  );

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    onSubmit: (): void => {},
  });

  useEffect(() => {
    if (!formData) {
      return;
    }

    formik.setValues({
      resignationDate: parseDateTime(formData.resDate, 'DD/MM/YYYY'),
      approxSettlementAmount: '',
      lastWorkingDate: parseDateTime(formData.lastWrkngDate, 'DD/MM/YYYY'),
      letterOfContent: formData.letterContent,
      reason: formData.reason,
    });

    getSettlementAmount(formData.resDate)
      .unwrap()
      .then((response) => {
        formik.setFieldValue('approxSettlementAmount', response);
      })
      .catch(() => {
        formik.setFieldValue('approxSettlementAmount', '');
      });
  }, [formData]);

  const handleApproveReject = useCallback(
    (action: FooterActions.APPROVE | FooterActions.REJECT): void => {
      const body: FormApproveRejectRequest = {
        taskId: String(taskId),
        command: action,
        comment: comments,
      };

      if (action === FooterActions.REJECT && !comments) {
        errorNotification('Please leave a comment');
        return;
      }

      const actionHandler = async (): Promise<void> => {
        try {
          await respondRequest(body).unwrap();

          if (action === FooterActions.APPROVE) {
            taskApprovedNotification();
          } else {
            taskRejectedNotification();
          }

          handleBackToListRequests();
        } catch {
          taskErrorNotification();
        }
      };

      actionHandler();
    },
    [comments, taskId, handleBackToListRequests]
  );

  const handleFooterAction = useCallback(
    (action: FooterActions, payload?: string) => {
      if (action === FooterActions.APPROVE || action === FooterActions.REJECT) {
        handleApproveReject(action);
      }

      if (action === FooterActions.COMMENTS_CHANGE) {
        setCommentValue(payload || '');
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }

      if (action === FooterActions.REASSIGN) {
        setReassignOpen(true);
      }
    },
    [handleBackToListRequests, handleApproveReject]
  );

  const dataSourceRequestDetails = useMemo(() => {
    const reasonDesc =
      reasonOptions.find((item) => item.value === formik.values.reason)
        ?.label || '';

    return [
      {
        label: 'Resignation Date',
        value: formik.values.resignationDate,
      },
      {
        label: 'Approx Settlement Amount',
        value: formik.values.approxSettlementAmount,
      },
      {
        label: 'Last Working Date',
        value: formik.values.lastWorkingDate,
      },
      {
        label: 'Letter Of Content',
        value: formik.values.letterOfContent || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'Reason',
        value: reasonDesc || EMPTY_FIELD_PLACEHOLDER,
      },
    ];
  }, [formik.values, reasonOptions]);

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        formType="approval"
        handleSubmit={formik.handleSubmit}
        userInfo={requestedUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
      >
        <FormItems>
          <ApproveList
            dataSource={dataSourceRequestDetails}
            label="Request Details"
          />
        </FormItems>
      </MainForm>
      <ReassignWindow
        creatorRequestNumber={requestedUser?.empNumber}
        taskId={taskId}
        isOpen={isReassignOpen}
        onClose={() => {
          setReassignOpen(false);
        }}
        onUserSelected={() => {
          setReassignOpen(false);
          handleBackToListRequests();
        }}
      />
    </Flex>
  );
};

export default ResignationRequestApproveForm;
